/* eslint-disable react/display-name */

import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useMemo, useState } from "react";
import { GridColDef } from "@material-ui/data-grid";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import BeeswaxReportsTableToolbar from "./BeeswaxReportsTableToolbar";
import Loader from "../../../../../components/elements/Loader";
import api from "../../../../../api";
import { BrandgenCampaignPool, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { RowType, transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

export type BeeswaxReportRow = {
  type: "campaign" | "line_item";
  id: string;
  name: string;
  timeFrame: string;
  impressions?: number | null;
  clicks?: number | null;
  spend?: number | null;
  ctr?: number | null;
  cpm?: number | null;
  cpc?: number | null;
  totalConversions?: number | null;
  totalRevenue?: number | null;
};

const beeswaxMetricsColumns = [
  { field: "impressions", headerName: "Impressions", width: 140, type: "FLAT" as const },
  { field: "clicks", headerName: "Clicks", width: 120, type: "FLAT" as const },
  { field: "spend", headerName: "Spent (USD)", width: 140, type: "CURRENCY" as const },
  { field: "ctr", headerName: "CTR", width: 120, type: "PERCENTAGE" as const },
  { field: "cpm", headerName: "CPM (USD)", width: 130, type: "CURRENCY" as const },
  { field: "cpc", headerName: "CPC (USD)", width: 130, type: "CURRENCY" as const },
  { field: "totalConversions", headerName: "Conversions", width: 140, type: "FLAT" as const },
  { field: "totalRevenue", headerName: "Revenue (USD)", width: 150, type: "CURRENCY" as const },
];

const beeswaxFloatFields = new Set(["spend", "ctr", "cpm", "cpc", "totalRevenue"]);

type BeeswaxReportsTableProps = {
  selectedTimeFrame: ReportTimeFrames;
  campaign: BrandgenCampaignPool;
};

const BeeswaxReportsTable: FC<BeeswaxReportsTableProps> = ({ selectedTimeFrame, campaign }) => {
  const classes = useCampaignDashboardStyles();
  const [rowsData, setRowsData] = useState<BeeswaxReportRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const fetchReports = async () => {
      try {
        setIsLoading(true);
        const { payload } = await api.getBeeswaxReports(campaign.brandgenCampaignPoolId);
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) setRowsData(list);
      } catch {
        if (!cancelled) setRowsData([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchReports();
    return () => {
      cancelled = true;
    };
  }, [campaign.brandgenCampaignPoolId]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "type",
        headerName: "Type",
        width: 130,
        renderCell: ({ value }) => (
          <Typography className={value === "campaign" ? classes.activeStatus : undefined}>
            {value === "campaign" ? "Campaign" : "Line Item"}
          </Typography>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        width: 320,
        renderCell: ({ value }) => (
          <Tooltip title={String(value)}>
            <Typography>{String(value)}</Typography>
          </Tooltip>
        ),
      },
      ...beeswaxMetricsColumns.map((col) => col),
    ],
    [classes],
  );

  const filteredRows = useMemo(
    () => rowsData.filter((report) => report.timeFrame === selectedTimeFrame),
    [rowsData, selectedTimeFrame],
  );

  const rows = useMemo(() => {
    return filteredRows.map((report, index) => {
      const baseRow: Record<string, unknown> = {
        id: report.id + index,
        type: report.type,
        name: report.name ?? "—",
      };
      for (const metric of beeswaxMetricsColumns) {
        const value = report[metric.field as keyof BeeswaxReportRow];
        const isFloat = beeswaxFloatFields.has(metric.field);
        baseRow[metric.field] =
          value !== undefined && value !== null
            ? transformRowValue(Number(value), metric.type === "FLAT", isFloat, metric.type as RowType)
            : "N/A";
      }
      return baseRow;
    });
  }, [filteredRows]);

  const isMaxRowsAmountExceeded = rows.length > 10;

  if (!isLoading && filteredRows.length === 0) {
    return null;
  }

  return (
    <div className={classes.campaignsLineItemsReportSection}>
      <div
        className={`${classes.lineItemsDashboard} ${
          isMaxRowsAmountExceeded ? classes.lineItemsDashboardFixedHeight : ""
        }`}
      >
        {isLoading ? (
          <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
        ) : (
          <BaseDataGrid
            components={{ Toolbar: BeeswaxReportsTableToolbar }}
            localeText={{ toolbarColumns: "Select Metrics" }}
            autoHeight={!isMaxRowsAmountExceeded}
            columns={columns}
            rows={rows.length ? rows : []}
          />
        )}
      </div>
    </div>
  );
};

export default BeeswaxReportsTable;
